import { ReportFieldItem, ReportItem, UserReportItem } from 'api/tenilo.reporting.api-client'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { useFormatMessage } from 'localization'
import { Colors } from 'styles/colors'
import Color from 'color'
import { IUserItem } from 'api/tillit.api-client'
import {
  getReportName, getReportDescription, createReportFilename,
  extractReportHeadersAndData, extractUserReportHeadersAndData, fillAndTextColorFromAlpha, FillAndTextColor
} from './report-helper';

export const saveReportToPdf = (gridWidth: number, report: ReportItem | undefined,
  userReport: UserReportItem | undefined,
  reportGridRef: React.MutableRefObject<HTMLElement | null>,
  exportDone: () => void): void => {
  // Note: exporting a html-element , e.g. table with many rows is very slow and produced a very large pdf.
  // use createReportTablePdf instead
  if (!reportGridRef?.current) {
    throw new Error("reportGridRef not set! Aborting")
  }

  const pdfDoc = new jsPDF({ orientation: gridWidth < 800 ? 'portrait' : 'landscape' })
  pdfDoc.setProperties({
    creator: 'tenilo.se',
    title: getReportName(report, userReport),
    subject: getReportDescription(report, userReport)
  })

  pdfDoc.html(reportGridRef.current, {
    callback: (d) => {
      d.save(createReportFilename(report, userReport, 'pdf'), { returnPromise: true }).then(() => {
        exportDone()
      })

    },
    margin: [10, 10, 10, 10],
    width: gridWidth < 800 ? 210 : 297, // 297=width mm A4 landscape,  // 210=width mm A4 portrait
    windowWidth: gridWidth + 300,
    autoPaging: true,

  })
}

export const createReportTablePdf = (f: ReturnType<typeof useFormatMessage>) => async (gridWidth: number, report: ReportItem | undefined,
  userReport: UserReportItem | undefined, dataRows: string[], alphaChannelRows: string[] | undefined, user: IUserItem, formFields: ReportFieldItem[] | undefined, sortedFieldItems: ReportFieldItem[] | undefined): Promise<void> => {
  const pdfDoc = new jsPDF({ orientation: gridWidth < 800 ? 'portrait' : 'landscape' })
  const fontSize = gridWidth < 800 ? 7 : 5
  pdfDoc.setProperties({
    creator: 'tenilo.se',
    title: getReportName(report, userReport),
    subject: getReportDescription(report, userReport)
  })

  let headersAndRows: any[] = []
  if (report) {
    const replaceCheckMark = true
    headersAndRows = extractReportHeadersAndData(f, report, dataRows, alphaChannelRows, user, replaceCheckMark, formFields, sortedFieldItems)
  }

  if (userReport) {
    headersAndRows = extractUserReportHeadersAndData(f, userReport, dataRows, alphaChannelRows, user, formFields, sortedFieldItems)
  }

  autoTable(pdfDoc, {
    theme: 'grid',
    styles: {
      fontSize,
    },
    headStyles: {
      fillColor: Color(Colors.TeniloGreen).string(),
      textColor: '#ffffff'
    },
    head: headersAndRows[0],
    body: headersAndRows[1],
    didDrawPage: (hookData) => {
      const base64Logo = 'data:image/png;base64,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'
      if (base64Logo) {
        const logoWidthUnits = 19
        const logoHeightUnits = 8
        hookData.doc.addImage(base64Logo, 'JPEG', hookData.doc.internal.pageSize.width - logoWidthUnits, 0, logoWidthUnits, logoHeightUnits);
      }
    },
    didParseCell: (hookData) => {
      if (hookData.row.index >= 0 && hookData.row.section !== 'head' && headersAndRows[2].length > 0) {
        const alphaRow = headersAndRows[2][hookData.row.index]
        const ftColor = fillAndTextColorFromAlpha(+(alphaRow[hookData.column.index])) as FillAndTextColor
        hookData.cell.styles.fillColor = ftColor.fillColor
        hookData.cell.styles.textColor = ftColor.textColor
      }
    }
  })

  pdfDoc.save(createReportFilename(report, userReport, 'pdf'))
}